'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { interpretAiSearch, type AiSearchSuggestion } from '@repo/api';
import { useAiSearchSuggestions } from './useAiSearchSuggestions';
import {
  getRecentAiSearches,
  saveRecentAiSearch,
  type RecentAiSearch,
} from '../utils/recentAiSearches';

export interface AiSearchController {
  query: string;
  setQuery: (value: string) => void;
  /** True while an `interpret` call (or the resulting navigation) is in flight. */
  isSubmitting: boolean;
  suggestions: AiSearchSuggestion[];
  recents: RecentAiSearch[];
  /** Re-read recent searches from storage (call on focus). */
  refreshRecents: () => void;
  /** Submit typed free text → interpret → navigate to the results URL. */
  submitTyped: (raw?: string) => void;
  /** Navigate straight to an already-resolved URL (suggestion / recent click). */
  goToUrl: (url: string, query?: string) => void;
}

/**
 * Shared brains for the AI search bar (desktop + mobile). Owns the query, the
 * starter suggestions, recent searches, and the submit/navigate flow.
 *
 * Only typed submits hit `interpret` (the paid, rate-limited endpoint). Clicking
 * a suggestion or a recent navigates straight to its stored URL — no round-trip.
 */
export function useAiSearchController(enabled: boolean): AiSearchController {
  const router = useRouter();
  const [query, setQuery] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [recents, setRecents] = React.useState<RecentAiSearch[]>([]);
  const suggestions = useAiSearchSuggestions(enabled);

  const refreshRecents = React.useCallback(() => {
    setRecents(getRecentAiSearches());
  }, []);

  const submitTyped = React.useCallback(
    (raw?: string) => {
      const q = (raw ?? query).trim();
      if (!q || isSubmitting) return;
      setIsSubmitting(true);
      void interpretAiSearch({ query: q, source: 'typed' })
        .then(res => {
          if (res.url) {
            saveRecentAiSearch({ query: q, url: res.url });
            router.push(res.url);
            // Leave `isSubmitting` on: navigation is underway, keep the spinner
            // until this bar unmounts on the results page.
          } else {
            setIsSubmitting(false);
          }
        })
        .catch(() => setIsSubmitting(false));
    },
    [query, isSubmitting, router],
  );

  const goToUrl = React.useCallback(
    (url: string, q?: string) => {
      if (!url) return;
      if (q) saveRecentAiSearch({ query: q, url });
      router.push(url);
    },
    [router],
  );

  return {
    query,
    setQuery,
    isSubmitting,
    suggestions,
    recents,
    refreshRecents,
    submitTyped,
    goToUrl,
  };
}

export default useAiSearchController;
